import { formatDate } from '@angular/common';
import { Injector, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { STComponent, STData, STReq, STRequestOptions } from '@delon/abc/st';
import { ACLService } from '@delon/acl';
import { _HttpClient, SettingsService } from '@delon/theme';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Observable, Subject } from 'rxjs';
import { ViewMode } from 'src/app/components/list-view-switcher/list-view-switcher.component';
import { EInvoiceService } from 'src/app/core/services/e-invoice.service';
import { DateFormatService } from 'src/app/shared/services/date-format.service';
import { OdataService } from 'src/app/shared/services/odata.service';

import { IFilter } from '../../../shared/types/filters';
import { EmailStatusService } from '@shared-feature/components/email-status-modal/email-status.service';

export abstract class DocListBaseService {
  constructor(private injector: Injector) {
    const dateFormatService = injector.get(DateFormatService);
    this.dateFormat =
      dateFormatService.currentDateFormatValue ||
      dateFormatService.getDefaultDateFormat();
    const router = injector.get(Router);
    const settings = injector.get(SettingsService);
    this.parkAndPost = settings.user['company']?.['isParkAndPostEnable'];
    router.events.subscribe((event: any) => {
      // On NavigationEnd
      if (event.type === 1) {
        const navigation = router.getCurrentNavigation();
        const state = navigation?.extras.state as any;

        if (state?.cloneData && state?.data) {
          this.triggerEvent({
            key: state?.key,
            type: 'clone',
            data: state?.data
          });
        }
      }
    });
  }

  private _dateFormat = '';
  get dateFormat(): string {
    const dateFormatService = this.injector.get(DateFormatService);
    return (
      dateFormatService.currentDateFormatValue ||
      this._dateFormat ||
      dateFormatService.getDefaultDateFormat()
    );
  }
  set dateFormat(value: string) {
    this._dateFormat = value;
  }
  public focusObserver = new Subject<void>();

  reportType = signal<any>(null);

  cloneData: any | null = null;
  eventTrigger: Subject<any> = new Subject<any>();

  abstract url: string;
  abstract expandProperties: string;
  abstract batchDelete(ids: any[]): Observable<any>;
  parkAndPost: boolean = false;

  st: STComponent | undefined;

  get acl() {
    return this.injector.get(ACLService);
  }

  get router() {
    return this.injector.get(Router);
  }

  get route() {
    return this.injector.get(ActivatedRoute);
  }

  get http() {
    return this.injector.get(_HttpClient);
  }

  get modalService() {
    return this.injector.get(NzModalService);
  }

  get msg() {
    return this.injector.get(NzMessageService);
  }

  get odata() {
    return this.injector.get(OdataService);
  }

  get eInvoiceService() {
    return this.injector.get(EInvoiceService);
  }

  get isEInvEnabled() {
    return this.eInvoiceService.isEnabled;
  }

  searchValue = '';
  deleting = false;
  loading = false;
  isConsoExist = false;
  aclKey: string | undefined;
  selectedRows: any[] = [];
  period: Date[] = [];
  status: any[] = ['All'];
  eInvStatus: any[] = [];
  eInvControl: any[] = [];
  searchColumns = ['DocCode', 'Description'];
  amountColumns = ['NetTotalAmount'];
  sortBy: string = 'docDate';
  sortDirection: 'desc' | 'asc' = 'desc';

  private _transactionView: ViewMode = 0; // 0: table, 1: card
  public transactionViewChanged: Subject<ViewMode> = new Subject<ViewMode>();
  public get transactionView(): ViewMode {
    return this._transactionView;
  }
  public set transactionView(v: ViewMode) {
    this._transactionView = v;
    this.transactionViewChanged.next(v);
  }
  filterColumns: IFilter[] = [];
  filterValues: any;

  req: STReq = {
    process: (options: STRequestOptions) => {
      let orderBy = '';
      if (this.sortBy === 'docDate') {
        orderBy = `docDate ${this.sortDirection}, docCode ${this.sortDirection}`;
      } else {
        orderBy = `${this.sortBy} ${this.sortDirection}`;
      }
      options.params = {
        ...options.params,
        $count: 'true',
        $expand: this.expandProperties,
        $orderby: orderBy
      };

      let filters: string[] = [];
      if (this.period && this.period.length === 2) {
        const start = formatDate(this.period[0], 'yyyy-MM-dd', 'en-US');
        const end = formatDate(this.period[1], 'yyyy-MM-dd', 'en-US');
        filters.push(`DocDate ge ${start} and DocDate le ${end}`);
      }

      if (!this.status.includes('All') && this.status.length > 0)
        if (this.status.length > 0) {
          // If it's a list of statuses, generate a filter with 'or' condition
          const statusFilters = this.status
            .map(status => `Status eq '${status}'`)
            .join(' or ');
          filters.push(`(${statusFilters})`);
        } else if (this.status) {
          // If it's a single status, generate a simple filter
          filters.push(`Status eq '${this.status}'`);
        }

      // Filter by e-Invoice status
      if (this.eInvStatus.length > 0) {
        const statusFilters = this.eInvStatus.map(status => {
          if (this.isConsoExist) {
            if (status === 'Pending') {
              return `(EInvoiceStatus eq '${status}' or (ConsolidatedUIN eq null and EInvoiceStatus eq null))`;
            }
            return `EInvoiceStatus eq '${status}' or ConsolidatedStatus eq '${status}'`;
          }
          if (status === 'Pending') {
            return `(EInvoiceStatus eq '${status}')`;
          }
          return `EInvoiceStatus eq '${status}'`;
        });
        filters.push(
          `((${statusFilters.join(' or ')}) and EInvoiceControl ne 'NA')`
        );
      }

      // Filter by e-Invoice Control
      if (this.eInvControl.length > 0) {
        const controlFilters = this.eInvControl.map(status => {
          return `EInvoiceControl eq '${status}'`;
        });
        filters.push(
          `((${controlFilters.join(' or ')}) and EInvoiceControl ne 'NA')`
        );
      }

      if (this.searchValue) {
        const searchFilters = this.odata.getSearchFilters(
          this.searchValue,
          this.searchColumns,
          this.amountColumns
        );
        filters = [...filters, ...searchFilters];
      }

      if (filters.length) {
        options.params['$filter'] = filters.map(x => `(${x})`).join(' and ');
      }
      if (this.filterValues) {
        try {
          const filterStr = this.odata.getFilterStringFromObject(
            this.filterValues as { [key: string]: string },
            this.filterColumns
          );
          if (filterStr) {
            options.params['$filter'] = options.params['$filter']
              ? `${options.params['$filter']} and ${filterStr}`
              : filterStr;
          }
        } catch (error) {
          console.log(error);
        }
      }
      return options;
    }
  };
  res = {
    process: (data: STData[], _: any) => {
      if (this.selectedRows.length) this.selectedRows = [];
      if (this.transactionView === 1) this.addGroups(data);
      return data;
    }
  };

  addGroups(items: any[]) {
    let group: any = undefined;
    const docItems = [...items];
    let currPos = 0;
    docItems.forEach((item: any, index) => {
      const month = formatDate(item.docDate, 'MMM, yyyy', 'en-US');
      if (group === undefined || group.month !== month) {
        group = {
          month: month,
          total: 0,
          outstanding: 0,
          count: 0,
          isGroup: true,
          disabled: true
        };
        items.splice(currPos, 0, group);
        currPos++;
      }
      group.total = group.total + item.netTotalAmountLocal;
      group.outstanding = group.outstanding + item.outstandingAmount;
      group.count = group.count + 1;
      currPos++;
    });
  }



  onOpenEmailStatusModal(docCode: string) {
    const srv = this.injector.get(EmailStatusService);
    srv.onOpenModal(docCode);
  }

  closeEmailStatusModal(){
    const srv = this.injector.get(EmailStatusService);
    srv.onCloseModal();
  }


  addRow(row: any) {
    if (this.st) {
      const groupTitle = 'NEW';
      let group: any = this.st._data.find((x: any) => x.month === groupTitle);
      if (!group) {
        group = {
          month: groupTitle,
          total: 0,
          outstanding: 0,
          count: 0,
          isGroup: true,
          disabled: true
        };
        this.st.addRow(group, { index: 0 });
      } else {
        group.count += 1;
      }

      this.st.addRow(row, { index: 1 });
    }
  }

  updateRow(row: any) {
    if (this.st) {
      const stRow = this.st._data.find(x => x['id'] === row.id);
      if (stRow) {
        Object.assign(stRow, row);
      }
    }
  }

  removeRow(row: any) {
    if (this.st) {
      const stRow = this.st._data.find(x => x['id'] === row.id);
      if (stRow) {
        this.st.removeRow(stRow);

        const month = formatDate(row.docDate, 'MMM, yyyy', 'en-US');
        let group: any = this.st._data.find(
          (x: any) => x.month === month || x.month === 'NEW'
        );
        if (group) {
          group.count--;
          if (group.count === 0) this.st.removeRow(group);
        }
      }
    }
  }

  notifySelection(data: any[]) {
    const res = data.filter(w => !w.disabled && w.checked === true);
    this.selectedRows = res;
  }

  gotoDetail(id: any) {
    const listUrl = window.location.href;
    let detailRoute = listUrl.substring(
      listUrl.indexOf('#') + 1,
      listUrl.lastIndexOf('/')
    );
    detailRoute += `/detail/${id}`;
    this.router.navigateByUrl(detailRoute);
  }

  onAction(action: string, data: any): Promise<boolean> {
    return new Promise(resolve => {
      resolve(true);
    });
  }

  reset() {
    this.selectedRows = [];
    this.deleting = false;
    this.period = [];
    this.searchValue = '';
  }

  triggerEvent(event: any) {
    this.eventTrigger.next(event);
  }

  showColumn(view: number) {
    return view === this.transactionView;
  }

  focus() {
    this.focusObserver.next();
  }
}
